import React from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import StatCard from '../../components/common/StatCard';
import {
  MOCK_ADMIN_KPIS, MOCK_REVENUE_CHART, MOCK_LEADS, MOCK_PROJECTS,
  MOCK_APPOINTMENTS, MOCK_LEADS_BY_SOURCE, MOCK_JOBS_BY_SERVICE,
} from '../../mock';

const PIE_COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b'];

const AdminDashboard: React.FC = () => {
  const recentLeads = MOCK_LEADS.slice(0, 5);
  const upcomingAppts = MOCK_APPOINTMENTS.slice(0, 3);
  const activeProjects = MOCK_PROJECTS.filter(p => p.status === 'in_progress');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
        {MOCK_ADMIN_KPIS.map(kpi => <StatCard key={kpi.id} {...kpi} />)}
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
        {/* Revenue Chart */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div>
              <h3 style={{ fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Revenue Overview</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>Monthly revenue vs target</p>
            </div>
            <select style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '0.375rem 0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)', outline: 'none' }}>
              <option>Last 6 months</option>
              <option>Last year</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={MOCK_REVENUE_CHART}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: any) => [`$${Number(v).toLocaleString()}`, '']} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '0.625rem' }} />
              <Area type="monotone" dataKey="value" name="Revenue" stroke="#f97316" strokeWidth={2.5} fill="url(#revGrad)" />
              <Area type="monotone" dataKey="value2" name="Target" stroke="#3b82f6" strokeWidth={2} strokeDasharray="4 4" fill="none" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Leads by Source */}
        <div className="card">
          <h3 style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>Leads by Source</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '1rem' }}>Month to date</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={MOCK_LEADS_BY_SOURCE} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={3}>
                {MOCK_LEADS_BY_SOURCE.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '0.625rem', fontSize: '0.8rem' }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
            {MOCK_LEADS_BY_SOURCE.map((item, i) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                {item.label} ({item.value})
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Jobs by Service */}
        <div className="card">
          <h3 style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1.25rem' }}>Jobs by Service Type</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={MOCK_JOBS_BY_SERVICE} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis dataKey="label" type="category" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={70} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '0.625rem', fontSize: '0.8rem' }} />
              <Bar dataKey="value" fill="#f97316" radius={[0, 4, 4, 0]}>
                {MOCK_JOBS_BY_SERVICE.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Active Projects */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Active Projects</h3>
            <Link to="/admin/projects" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>View All</Link>
          </div>
          {activeProjects.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No active projects.</p>
          ) : activeProjects.map(p => (
            <div key={p.id} style={{ marginBottom: '1rem', padding: '0.875rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', margin: 0 }}>{p.title}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>{p.customerName}</p>
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#f97316' }}>{p.progress}%</span>
              </div>
              <div style={{ background: 'var(--border-color)', borderRadius: '9999px', height: '5px', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(90deg, #f97316, #ea580c)', height: '100%', width: `${p.progress}%`, borderRadius: '9999px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Recent Leads */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Recent Leads</h3>
            <Link to="/admin/leads" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>View All</Link>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  {['Name', 'Service', 'Value', 'Status'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border-color)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentLeads.map(lead => (
                  <tr key={lead.id}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{lead.name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{lead.email}</div>
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                      {lead.service.replace('-', ' ')}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)', fontWeight: 700, color: '#f97316' }}>
                      ${lead.estimatedValue.toLocaleString()}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>
                      <span className={`badge ${lead.status === 'won' ? 'badge-success' : lead.status === 'lost' ? 'badge-danger' : lead.status === 'new' ? 'badge-orange' : 'badge-info'}`} style={{ textTransform: 'capitalize' }}>
                        {lead.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Today's Appointments */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Upcoming Appointments</h3>
            <Link to="/admin/appointments" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>View All</Link>
          </div>
          {upcomingAppts.map(appt => (
            <div key={appt.id} style={{ display: 'flex', gap: '0.875rem', marginBottom: '1rem', padding: '0.875rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', alignItems: 'center' }}>
              <div style={{ width: '3rem', height: '3rem', borderRadius: '0.625rem', background: 'rgb(249 115 22 / 0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '0.65rem', color: '#f97316', fontWeight: 700, lineHeight: 1 }}>
                  {new Date(appt.date).toLocaleString('default', { month: 'short' }).toUpperCase()}
                </span>
                <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#f97316', lineHeight: 1.1 }}>
                  {new Date(appt.date).getDate()}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{appt.customerName}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.2rem 0 0', textTransform: 'capitalize' }}>
                  {appt.type} · {appt.timeSlot}
                </p>
              </div>
              <span className={`badge ${appt.status === 'confirmed' ? 'badge-success' : 'badge-warning'}`}>{appt.status}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media(max-width:900px){
          div[style*="grid-template-columns: 2fr 1fr"]{grid-template-columns:1fr!important}
          div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}
          div[style*="repeat(auto-fill, minmax(240px, 1fr))"]{grid-template-columns:repeat(2,1fr)!important}
        }
        @media(max-width:640px){
          div[style*="repeat(auto-fill, minmax(240px, 1fr))"]{grid-template-columns:1fr!important}
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
